import { useCallback, useEffect, useRef, useState } from 'react';
import { createLogStream } from '../realtime/sse/createLogStream';
import { initialAgents } from '../mocks/agents';
import useAppStore from '../store';

const DEMO_VERBS = ['reranked', 'embedded', 'fetched', 'validated', 'dispatched', 'summarized', 'scraped', 'queued'];
const DEMO_TARGETS = ['client ingestion corpus', 'campaign brief v3', 'ledger sync payload', 'support macro set', 'repo webhook hook', 'scrape queue shard'];

let demoSeq = 0;

function makeDemoEntry(agent) {
  const roll = Math.random();
  const level = roll < 0.68 ? 'info' : roll < 0.82 ? 'debug' : roll < 0.92 ? 'warn' : 'error';
  const verb = DEMO_VERBS[Math.floor(Math.random() * DEMO_VERBS.length)];
  const target = DEMO_TARGETS[Math.floor(Math.random() * DEMO_TARGETS)];

  return {
    id: `demo-${demoSeq++}`,
    agentId: agent.id,
    agent: agent.name,
    level,
    message: `${agent.name} ${verb} ${target}${level === 'error' ? ' — retry 1/3' : ''}`,
    timestamp: Date.now()
  };
}

// Live SSE attach is OFF until a backend /api/console/logs route exists
// (backend/routes/index.js keeps console UNREGISTERED — no queryable log
// source yet). With this false the hook runs the honest DEMO stream
// without firing a doomed probe (a phantom 404 per Console mount). Flip
// to true when that route ships.
const LIVE_CONSOLE_LOGS_ENABLED = false;

/**
 * Console log feed (T12.3). Attaches live SSE (realtime/sse/createLogStream,
 * /api/console/logs) only when LIVE_CONSOLE_LOGS_ENABLED — the backend route
 * is unregistered, so otherwise the hook runs the synthesized demo stream
 * directly instead of probing for a doomed 404. Entries are batched (250ms)
 * before hitting the store so 10k+ lines don't trigger a render per line.
 */
function useConsoleLogStream({ paused }) {
  const appendLogs = useAppStore((state) => state.appendLogs);
  const setLogFeedMode = useAppStore((state) => state.setLogFeedMode);
  const [feedMode, setFeedMode] = useState('connecting');
  const bufferRef = useRef([]);
  const flushTimerRef = useRef(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const push = useCallback((entries) => {
    bufferRef.current.push(...entries);
    if (flushTimerRef.current === null) {
      flushTimerRef.current = window.setTimeout(() => {
        flushTimerRef.current = null;
        if (pausedRef.current) {
          bufferRef.current = [];
          return;
        }
        if (bufferRef.current.length > 0) {
          appendLogs(bufferRef.current);
          bufferRef.current = [];
        }
      }, 250);
    }
  }, [appendLogs]);

  useEffect(() => {
    let stream = null;
    let demoTimer = null;
    let cancelled = false;

    const startDemo = () => {
      if (cancelled) return;
      setFeedMode('demo');
      setLogFeedMode('demo');
      demoTimer = window.setInterval(() => {
        const agent = initialAgents[Math.floor(Math.random() * initialAgents.length)];
        push([makeDemoEntry(agent)]);
      }, 550);
    };

    const attemptLive = () => {
      try {
        stream = createLogStream('/api/console/logs', {
          onMessage: (entry) => push([entry]),
          onError: () => {
            stream?.close();
            stream = null;
            startDemo();
          }
        });
        setFeedMode('live');
        setLogFeedMode('live');
      } catch {
        startDemo();
      }
    };

    if (LIVE_CONSOLE_LOGS_ENABLED) {
      attemptLive();
    } else {
      startDemo();
    }

    return () => {
      cancelled = true;
      window.clearInterval(demoTimer);
      if (flushTimerRef.current !== null) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      stream?.close();
    };
    // Reconnect only on unmount/remount; push identity is stable.
  }, []);

  return feedMode;
}

export default useConsoleLogStream;
